/**
 * Reload Token Service
 * Handles all reload token-related API calls
 */

import { apiClient, HeaderType } from "@/lib/api-client";
import { createSSEConnection } from "@/lib/sse-client";
import type { SSESubscription } from "@/lib/sse-client";

export interface ReloadToken {
  token: string;
  expired?: string;
  station_id: number;
}

export interface TokenEventData {
  phone: string;
  store_id: number;
  store_name: string | null;
  inserted_at: string;
}

/**
 * Callbacks for SSE subscription
 */
export interface SSECallbacks {
  onMessage: (data: TokenEventData) => void;
  onError?: (error: Error) => void;
  onOpen?: () => void;
}

/**
 * Reload Token Service
 * Encapsulates all reload token operations
 *
 * @example Usage with useAuth()
 * ```tsx
 * import { useAuth } from '@/hooks/useAuth';
 * import { ReloadTokenService } from '@/services';
 *
 * function MyComponent() {
 *   const { userId } = useAuth();
 *
 *   const { data: token } = useQuery({
 *     queryKey: ['reloadToken', userId],
 *     queryFn: () => ReloadTokenService.getMyToken(userId!),
 *     enabled: !!userId,
 *   });
 *
 *   return <div>Token: {token}</div>;
 * }
 * ```
 */
export class ReloadTokenService {
  /**
   * Get current user's reload token
   *
   * @param userId - User ID from useAuth() hook
   * @returns Promise that resolves with token string
   *
   * @example
   * ```typescript
   * const { userId } = useAuth();
   * if (userId) {
   *   const token = await ReloadTokenService.getMyToken(userId);
   * }
   * ```
   */
  static async getMyToken(userId: string): Promise<string> {
    const response = await apiClient.get<ReloadToken>(
      `/users/${userId}/reload-token`,
      {
        headerTypes: [HeaderType.SERVER_AUTH],
      },
    );
    return response.token;
  }

  /**
   * Subscribe to token status changes via Server-Sent Events (SSE)
   * Uses generic SSE client with proper Authorization header support
   *
   * @param userId - User ID from useAuth() hook
   * @param token - The token to subscribe to
   * @param callbacks - Event callbacks (onMessage, onError, onOpen)
   * @returns SSESubscription with abort() method for cleanup
   *
   * @example
   * ```typescript
   * const { userId } = useAuth();
   * if (userId) {
   *   const subscription = ReloadTokenService.subscribeToTokenEvents(
   *     userId,
   *     'token-123',
   *     {
   *       onMessage: (data) => {
   *         console.log('Token processed:', data);
   *         subscription.abort(); // Close connection after receiving event
   *       },
   *       onError: (error) => {
   *         console.error('SSE error:', error);
   *       },
   *       onOpen: () => {
   *         console.log('SSE connection opened');
   *       }
   *     }
   *   );
   *
   *   // Cleanup on unmount
   *   return () => subscription.abort();
   * }
   * ```
   */
  static subscribeToTokenEvents(
    userId: string,
    token: string,
    callbacks: SSECallbacks,
  ): SSESubscription {
    return createSSEConnection<TokenEventData>({
      endpoint: `/users/${userId}/reload-token/event`,
      params: { token },
      eventType: 'token_used',
      onMessage: callbacks.onMessage,
      onError: callbacks.onError,
      onOpen: callbacks.onOpen,
    });
  }
}
